"use client";
import Image from "next/image";
import React, { useMemo, useState, type JSX } from "react";
import { cn, getVariant } from "@/lib/utils";
import UpArrowError from "@icons/arrow/up-red.svg";
import DownArrowError from "@icons/arrow/down-red.svg";
import { variantType } from "@/types";

interface MessageProps {
  message: string | JSX.Element;
  iconClassName?: string;
  messageClassName?: string;
  className?: string;
  variant?: variantType;
  expandTitle?: string | JSX.Element;
  expandDescription?: string | JSX.Element;
  ariaId?: string;
  hideErrorIcon?: boolean;
  isFormError?: boolean;
}

const generateUniqueId = (() => {
  let counter = 0;
  return () => `message-${counter++}`;
})();

const Message: React.FC<MessageProps> = ({
  message,
  iconClassName,
  variant = "error",
  expandTitle,
  expandDescription,
  messageClassName,
  className,
  ariaId,
  hideErrorIcon = false,
  isFormError,
}) => {
  const isSuccess = variant === "success" || variant === "checkSuccess";
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const id = useMemo<string>(() => generateUniqueId(), []);

  const getIconAlt = () => {
    if (variant === "success") {
      return "note";
    } else {
      return `${variant} icon`;
    }
  };

  return (
    <>
      <div
        id={ariaId ?? `error-message-${id}`}
        className={`flex ${className}`}
        tabIndex={-1}
        {...(isFormError
          ? {}
          : {
              role: "alert",
              "aria-live": isSuccess ? "polite" : "assertive",
              "aria-atomic": true,
            })}
      >
        {!hideErrorIcon && <Image aria-hidden="true" className={cn(iconClassName, "mb-1")} src={getVariant(variant)} alt={getIconAlt()} />}
        {typeof message === "string" ? (
          <p
            className={cn(
              "ml-2 !font-normal text-[16px] tracking-[-0.32px] leading-[22px]",
              isSuccess ? "text-success" : "text-error",
              messageClassName,
            )}
          >
            {message}
          </p>
        ) : (
          message
        )}
        {expandTitle && (
          <button
            type="button"
            className={"flex text-error font-bold ml-1  cursor-pointer mb-2"}
            onClick={() => setIsExpanded(!isExpanded)}
            aria-expanded={isExpanded}
            aria-controls={`expandDescription${id}`}
          >
            <p className="mr-1.5 font-epilogue text-base leading-[22px]">{expandTitle}</p>
            <Image className="text-error" alt="expand options icon" src={isExpanded ? UpArrowError : DownArrowError} />
          </button>
        )}
      </div>
      {isExpanded && (
        <div id={`expandDescription${id}`} className="mt-2">
          {expandDescription}
        </div>
      )}
    </>
  );
};

export default Message;
